import { LocalNotifications } from '@capacitor/local-notifications';
import { Geolocation } from '@capacitor/geolocation';
import { registerPlugin, Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { NativeSettings, AndroidSettings, IOSSettings } from 'capacitor-native-settings';
import Swal from 'sweetalert2';

// The background geolocation plugin needs to be registered
const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');
// Register our custom plugin for strict Android background permission checking
const LocationPermissionChecker = registerPlugin('LocationPermissionChecker');

let isAppStateListenerAdded = false;
let isTrackingInitialized = false;
let isAppInForeground = true;

App.addListener('appStateChange', ({ isActive }) => {
    isAppInForeground = isActive;
});

const initializeTracking = async () => {
    if (isTrackingInitialized) return;
    isTrackingInitialized = true;

    try {
        // Add background watcher
        BackgroundGeolocation.addWatcher(
            {
                backgroundMessage: 'App is running in the background to track location.',
                backgroundTitle: 'Location Tracking Active',
                requestPermissions: false,
                stale: false,
                distanceFilter: 0 // set to 0 to fire constantly even when stationary
            },
            function callback(location, error) {
                if (error) {
                    if (error.code === 'NOT_AUTHORIZED') {
                        showPermissionAlert();
                    }
                    console.error('Background Geolocation Error:', error);
                    return;
                }
                console.log('Background Location Update:', location);

                // NATIVE HTTP PLUGIN: Send to server unconditionally (foreground and background)
                const now = new Date().getTime();
                const lastPing = parseInt(localStorage.getItem('last_bg_ping') || '0', 10);

                if (now - lastPing >= 300000) { // 5 minutes
                    localStorage.setItem('last_bg_ping', now.toString());

                    const userStr = localStorage.getItem('user');
                    let user_id = null;
                    if (userStr) {
                        try {
                            user_id = JSON.parse(userStr).id;
                        } catch (e) { }
                    }
                    if (user_id) {
                        import('../api/axios').then(({ default: api }) => {
                            api.post('/real-time-location', {
                                user_id: user_id,
                                lat: location.latitude,
                                lng: location.longitude,
                                state: isAppInForeground ? 'foreground' : 'background'
                            }, { skipLoader: true })
                                .then(response => console.log('API Response:', response.status))
                                .catch(err => {
                                    console.error('API Error:', err.message);
                                    if (err.response && err.response.data) {
                                        console.error('API Error Details:', JSON.stringify(err.response.data));
                                    }
                                });
                        });
                    }
                }
            }
        ).then(watcher_id => {
            console.log('Background Geolocation started, watcher_id:', watcher_id);
        }).catch(err => {
            console.error('Background Geolocation watcher failed:', err);
            showPermissionAlert();
        });
    } catch (e) {
        console.error('Failed to start background tracking:', e);
    }
};

export const startBackgroundLocationTracking = async () => {
    if (Capacitor.getPlatform() !== 'android') {
        return;
    }

    if (!isAppStateListenerAdded) {
        isAppStateListenerAdded = true;

        try {
            let permStatus = await LocalNotifications.checkPermissions();
            if (permStatus.display !== 'granted') {
                permStatus = await LocalNotifications.requestPermissions();
                if (permStatus.display !== 'granted') {
                    await Swal.fire({
                        title: 'Notifications Disabled',
                        text: 'Please enable notifications in settings to receive tracking updates.',
                        icon: 'info',
                        confirmButtonText: 'Okay'
                    });
                }
            }
        } catch (e) {
            console.log('LocalNotifications permission check failed:', e);
        }

        const checkPerms = async () => {
            try {
                const bgPermission = await LocationPermissionChecker.checkBackgroundPermission();
                if (!bgPermission.granted) {
                    if (typeof Swal.isVisible === 'function' && !Swal.isVisible()) {
                        showPermissionAlert();
                    }
                } else {
                    if (typeof Swal.isVisible === 'function' && Swal.isVisible()) {
                        const titleEl = Swal.getTitle();
                        if (titleEl && titleEl.textContent === 'Location Permission Required') {
                            Swal.close();
                        }
                    }
                    // Location is granted, start tracking and ask for notification permissions
                    initializeTracking();
                }
            } catch (e) {
                console.error('Permission check error:', e);
            }
        };

        // Continuously check every 2 seconds
        setInterval(checkPerms, 2000);
        // Fire immediately on first call
        checkPerms();
    }
};

const showPermissionAlert = () => {
    Swal.fire({
        title: 'Location Permission Required',
        html: `
        <style>
          .custom-swal-popup {
            border-radius: 16px !important;
            padding: 32px 24px 24px 24px !important;
            font-family: 'Outfit', sans-serif !important;
            width: 440px !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
          }
          .custom-swal-actions {
            display: flex !important;
            gap: 12px !important;
            margin-top: 24px !important;
            width: 100% !important;
            padding: 0 !important;
          }
          .custom-swal-confirm {
            background: linear-gradient(135deg, #f97316 0%, #ea580c 100%) !important;
            color: white !important;
            border: none !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            gap: 8px !important;
          }
          .custom-swal-confirm:hover {
            opacity: 0.9 !important;
          }
          .custom-swal-cancel {
            background-color: #ffffff !important;
            color: #475569 !important;
            border: 1px solid #cbd5e1 !important;
            border-radius: 8px !important;
            padding: 10px 24px !important;
            font-size: 14px !important;
            font-weight: 600 !important;
            flex: 1 !important;
            cursor: pointer !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          .custom-swal-cancel:hover {
            background-color: #f1f5f9 !important;
          }
        </style>
        <p style="color: #64748b; font-size: 14px; margin-top: 8px;">
            This app requires "Always Allow" (or "Allow all the time") location access. You will be redirected to Settings. Please enable it, then reopen the app.
        </p>
        `,
        icon: 'warning',
        showCancelButton: true,
        cancelButtonText: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px;"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg> Exit App',
        confirmButtonText: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="margin-right: 6px;"><circle cx="12" cy="12" r="3"></circle><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path></svg> Open Settings',
        buttonsStyling: false,
        customClass: {
            popup: 'custom-swal-popup',
            actions: 'custom-swal-actions',
            cancelButton: 'custom-swal-cancel',
            confirmButton: 'custom-swal-confirm',
        },
        allowOutsideClick: false,
        allowEscapeKey: false
    }).then((result) => {
        if (Capacitor.isNativePlatform()) {
            if (result.isConfirmed) {
                NativeSettings.open({
                    optionAndroid: AndroidSettings.ApplicationDetails,
                    optionIOS: IOSSettings.App
                });
            } else if (result.dismiss === Swal.DismissReason.cancel) {
                App.exitApp();
            }
        }
    });
};
